<script setup lang="ts">
import type { Schema } from '@/modules/ui/models/schema/schema.ts';
import { computed, nextTick, ref, watch } from 'vue';
import { get, set } from 'lodash-es';
import type { I18TParams } from '@/i18n.ts';
import { useFormTooltips } from '@/modules/ui/composables/use-form-tooltips.ts';
import UiField from '@/modules/ui/components/ui/UiField.vue';
import UiExpandTransition from '@/modules/ui/components/ui/UiExpandTransition.vue';
import { ValueUtil } from '@/modules/common/utils/value.util.ts';

type Model = { [key: string]: unknown };

const model = defineModel<Model>({ required: true });

const isDirty = defineModel<boolean>('dirty', { default: false });
const isValid = defineModel<boolean>('valid', { default: false });
const showErrors = defineModel<boolean>('showErrors', { default: false });

const {
  type = 'default',
  schema,
  noTransition,
  expandTransition,
  hideFieldErrorMessages = false,
} = defineProps<{
  type?: 'default' | 'paddings' | 'compact';
  schema: Schema[];
  disabled?: boolean;
  noTransition?: boolean;
  expandTransition?: boolean;
  hideFieldErrorMessages?: boolean;
}>();

const emit = defineEmits<{
  submit: [formData: Model];
}>();

const fields = ref<{ [key: string]: unknown }>({});
const openIndex = ref<number | null>(null);

const flex = computed(() => schema.some((field) => field.indent === 'spread'));

const fieldsWrapper = computed(() => {
  if (noTransition) {
    return { is: 'div', props: {} };
  }

  if (expandTransition) {
    return { is: UiExpandTransition, props: { group: true } };
  }

  return { is: 'TransitionGroup', props: { name: flex.value ? 'opacity' : 'list' } };
});

const errors = computed(() =>
  schema.reduce((acc: { [key: string]: string | null | I18TParams }, field) => {
    for (const validator of field.validators) {
      const error = validator(get(model.value, field.name) as never);

      if (error) {
        return { ...acc, [field.id]: error };
      }
    }
    return acc;
  }, {})
);

watch(errors, (errors) => (isValid.value = !Object.values(errors).some(Boolean)), { immediate: true });

function submit() {
  showErrors.value = !isValid.value;

  if (isValid.value) {
    emit('submit', model.value);
  }
}

watch(model, () => (isDirty.value = true));

watch(showErrors, (showErrors) => {
  if (!showErrors) {
    return;
  }

  const id = Object.keys(errors.value)[0];

  if (!id) {
    return;
  }

  const element = document.getElementById(id);

  if (!element) {
    return;
  }

  element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
});

let proxy: Model = { ...model.value };
let skipModelSync = false;

watch(model, (value) => {
  if (skipModelSync) {
    return;
  }

  proxy = { ...value };
});

function setValue(fieldName: string, value: unknown) {
  const next = { ...proxy };
  set(next, fieldName, value);
  proxy = next;
  skipModelSync = true;
  model.value = next;
  nextTick(() => {
    skipModelSync = false;
  });
}

function getValue<T>(fieldName: string) {
  return get(model.value, fieldName) as T;
}

function onValueUpdate(field: Schema, value: unknown) {
  setValue(field.name, value);
}

function onLabelClick(event: MouseEvent, field: Schema, index: number) {
  if (field.collapsible) {
    event.preventDefault();
    openIndex.value = openIndex.value === index ? null : index;
    return;
  }

  if (field.focusOnLabelClick) {
    (fields.value[field.name] as { focus?: () => void } | undefined)?.focus?.();
  }
}

function getField<T extends abstract new (...args: unknown[]) => unknown>(name: string): InstanceType<T> | undefined {
  return fields.value[name] as InstanceType<T> | undefined;
}

function getCount(field: Schema) {
  const value = model.value[field.name];

  if (field.showCount && Array.isArray(value)) {
    return value.length;
  }
}

function getWithTooltip(field: Schema) {
  return !field.label && 'placeholder' in field && !!field.placeholder;
}

function getTooltip(field: Schema) {
  if (getWithTooltip(field) && !ValueUtil.isEmpty(getValue(field.name)) && 'placeholder' in field) {
    return field.placeholder;
  }
}

function getPlaceholder(field: Schema) {
  if (!('placeholder' in field)) {
    return undefined;
  }

  if (field.readonly) {
    return `ui.common.placeholder.notSpecified.${field.gender ?? 'n'}`;
  }

  return field.placeholder;
}

const { tooltipsDisabled, onInput, onFocus, onBlur } = useFormTooltips();

defineExpose({ submit, getField, setValue, getValue });
</script>

<template>
  <form
    class="ui-form"
    :class="[`_type-${type}`, { _flex: flex }]"
    @submit.prevent
  >
    <component
      :is="fieldsWrapper.is"
      v-bind="fieldsWrapper.props"
      class="ui-form__fields"
      :class="{ _plain: noTransition }"
    >
      <UiField
        v-for="(field, index) in schema"
        :id="field.id"
        :key="field.name"
        :class="[`_type-${type}`, `_indent-${field.indent}`]"
        :label="field.label"
        :error="showErrors && !hideFieldErrorMessages ? errors[field.id] : null"
        :invalid="hideFieldErrorMessages && showErrors && !!errors[field.id]"
        :required="field.required"
        :collapsible="field.collapsible"
        :open="index === openIndex"
        :count="getCount(field)"
        :tooltip="getTooltip(field)"
        :with-tooltip="getWithTooltip(field)"
        :tooltip-disabled="tooltipsDisabled"
        @label-click="onLabelClick($event, field, index)"
      >
        <component
          :is="field.component"
          :ref="(element: unknown) => (fields[field.name] = element)"
          v-bind="field"
          :label="field.label"
          :placeholder="getPlaceholder(field)"
          :error="showErrors && !!errors[field.id]"
          :disabled="disabled || field.disabled || field.aiGenerating.value"
          :model-value="getValue(field.name) as any"
          @update:model-value="onValueUpdate(field, $event)"
          @input="onInput"
          @focus="onFocus"
          @blur="onBlur"
        >
          <template
            v-if="field.slot"
            #[field.slot.to]="scope"
          >
            <slot
              :name="field.slot.from"
              :scope="scope"
              :field="field"
            />
          </template>
        </component>
      </UiField>
    </component>
  </form>
</template>

<style scoped lang="scss">
.ui-form {
  position: relative;

  &._flex {
    display: flex;
    flex-direction: column;

    > .ui-form__fields {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      width: 100%;
      min-width: 0;
      min-height: 0;
    }
  }

  &._type-paddings {
    padding: 8px 0;
  }
}

.ui-form__fields {
  &._plain {
    display: contents;
  }
}

.ui-field {
  max-width: 100%;

  &._type-default,
  &._type-paddings {
    &:not(:last-child) {
      margin-bottom: 12px;

      &._indent-spacer {
        margin-bottom: 24px;
      }

      &._indent-spread {
        margin-bottom: auto;
      }
    }
  }

  &._type-compact {
    &:not(:last-child) {
      margin-bottom: 8px;

      &._indent-spacer {
        margin-bottom: 16px;
      }

      &._indent-spread {
        margin-bottom: auto;
      }
    }
  }
}
</style>
